import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Server, Loader } from 'lucide-react';
import api from '../utils/api';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paypalOrderId = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [server, setServer] = useState(null);

  useEffect(() => {
    const verify = async () => {
      try {
        if (sessionId) {
          const { data } = await api.post('/payments/stripe/verify', { session_id: sessionId });
          setServer(data.server);
        } else if (paypalOrderId) {
          const { data } = await api.post('/payments/paypal/capture', { order_id: paypalOrderId });
          setServer(data.server);
        } else {
          setStatus('error');
          return;
        }
        setStatus('success');
      } catch {
        setStatus('error');
      }
    };
    verify();
  }, [sessionId, paypalOrderId]);

  if (status === 'loading') return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="text-center">
        <Loader className="w-12 h-12 text-green-400 animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Setting up your server...</p>
      </div>
    </div>
  );

  if (status === 'error') return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="text-5xl mb-4">⚠️</div>
        <h1 className="text-2xl font-black text-white mb-2">Something went wrong</h1>
        <p className="text-gray-400 mb-6">Your payment may have gone through but we couldn't confirm it. Please contact support and we'll sort it out immediately.</p>
        <Link to="/dashboard" className="bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-6 py-3 rounded-xl transition-colors">
          Go to Dashboard
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        {/* Success animation */}
        <div className="w-20 h-20 bg-green-500/10 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>

        <h1 className="text-3xl font-black text-white mb-2">You're in the grid! 🎮</h1>
        <p className="text-gray-400 mb-8">
          Your payment was successful and your Minecraft server is being set up right now.
        </p>

        {server && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 mb-8 text-left">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
                <Server className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="font-bold text-white">{server.name}</div>
                <div className="text-gray-500 text-sm">Installing...</div>
              </div>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 text-xs text-yellow-400">
              ⏱ Your server will be ready in about 30 seconds. Check your dashboard for live status.
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/dashboard"
            className="bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Go to Dashboard
          </Link>
          <a
            href="https://discord.gg/plarixmc"
            target="_blank"
            rel="noreferrer"
            className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-xl transition-colors"
          >
            Join Discord
          </a>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
